import { ref, reactive, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { CalendarEvent } from '@/common/model/events';
import { EventLocation } from '@/common/model/location';
import type { Calendar } from '@/common/model/calendar';
import CalendarService from '@/client/service/calendar';
import EventService from '@/client/service/event';
import CategoryService from '@/client/service/category';
import SeriesService from '@/client/service/series';
import ModelService from '@/client/service/models';
import { useCalendarStore } from '@/client/stores/calendarStore';
import { useEventDuplication } from '@/client/composables/useEventDuplication';
import { useToast } from '@/client/composables/useToast';

/**
 * Editor mode type
 */
export type EditorMode = 'create' | 'edit' | 'duplicate';

/**
 * Event editor state interface
 */
export interface EventEditorState {
  isLoading: boolean;
  /**
   * Load failures (event never loaded) hold an `event_editor.editor`
   * translation key, which edit_event.vue's load-error view resolves so the
   * message follows the interface language. Once the event has loaded, the
   * form's error box renders `err` as text, so save errors and later
   * initialization failures hold translated text.
   */
  err: string;
  errDetail: string;
  event: CalendarEvent | null;
  calendar: Calendar | null;
  availableCalendars: Calendar[];
  mode: EditorMode;
  isDuplicationMode: boolean;
}

/**
 * Checks whether an EventLocation has any meaningful data.
 * A location is considered empty if it has no id and no name.
 *
 * @param location - The location to check
 * @returns true if the location is empty
 */
function isEmptyLocation(location: EventLocation): boolean {
  return !location.id && !location.name;
}

/**
 * Composable for event editor core functionality
 *
 * Handles mode determination, event initialization, loading, and saving.
 * Extracted from edit_event.vue for better maintainability and testability.
 *
 * @param defaultLanguage - The default language code for event content
 * @returns Event editor state and methods
 */
export function useEventEditor(defaultLanguage: string = 'en') {
  const route = useRoute();
  const router = useRouter();
  const calendarStore = useCalendarStore();
  const { stripEventForDuplication } = useEventDuplication();
  const toast = useToast();

  // Services
  const calendarService = new CalendarService();
  const eventService = new EventService();
  const categoryService = new CategoryService();
  const seriesService = new SeriesService();

  // State
  const state = reactive<EventEditorState>({
    isLoading: true,
    err: '',
    errDetail: '',
    event: null,
    calendar: null,
    availableCalendars: [],
    mode: 'create',
    isDuplicationMode: false,
  });

  const selectedCategories = ref<string[]>([]);
  const selectedSeriesId = ref<string | null>(null);
  const mediaId = ref<string | null>(null);

  // Field-level validation errors keyed by field name
  const fieldErrors = reactive<Record<string, string>>({});

  /**
   * Clear a specific field error when the user modifies the field
   *
   * @param fieldName - The field name to clear the error for
   */
  const clearFieldError = (fieldName: string): void => {
    delete fieldErrors[fieldName];
  };

  /**
   * Clear all field errors
   */
  const clearAllFieldErrors = (): void => {
    for (const key of Object.keys(fieldErrors)) {
      delete fieldErrors[key];
    }
  };

  /**
   * Validate the event before saving.
   * Checks that required fields are populated and populates fieldErrors.
   *
   * @param t - Translation function for error messages
   * @returns true if the event is valid
   */
  const validateEvent = (t: (key: string) => string): boolean => {
    clearAllFieldErrors();

    const model = state.event;
    if (!model) {
      return false;
    }

    // Title required: at least one language must have a non-empty title
    const languages = model.getLanguages();
    const hasTitle = languages.some((lang: string) => {
      const name = model.content(lang).name;
      return name && name.trim().length > 0;
    });
    if (!hasTitle) {
      fieldErrors.title = t('error_title_required');
    }

    // Schedule required: at least one schedule must have a start date
    const hasValidSchedule = model.schedules && model.schedules.some(
      (schedule: any) => schedule.startDate,
    );
    if (!hasValidSchedule) {
      fieldErrors.schedule = t('error_date_required');
    }

    return Object.keys(fieldErrors).length === 0;
  };

  /**
   * Determine the editor mode based on route parameters
   * - Edit mode: /event/:eventId
   * - Duplicate mode: /event?from=:eventId
   * - Create mode: /event (default)
   *
   * @param eventIdProp - Optional event ID from component props
   * @returns The determined editor mode
   */
  const determineMode = (eventIdProp?: string | null): EditorMode => {
    const eventIdParam = eventIdProp || route.params.eventId;
    const fromParam = route.query.from;

    if (eventIdParam) {
      return 'edit';
    }
    if (fromParam) {
      return 'duplicate';
    }
    return 'create';
  };

  /**
   * Initialize a new event for create mode
   *
   * @param calendar - The calendar to associate the event with
   * @returns A new CalendarEvent instance
   */
  const initializeNewEvent = (calendar?: Calendar): CalendarEvent => {
    const event = new CalendarEvent();
    event.location = new EventLocation();
    event.addSchedule();
    if (calendar) {
      event.calendarId = calendar.id;
    }
    // Pre-populate default language content to avoid triggering dirty state when template renders
    event.content(defaultLanguage);
    return event;
  };

  /**
   * Load an event from the API for edit or duplicate mode
   * Uses ModelService which includes JWT authentication via axios interceptor
   *
   * @param eventId - The ID of the event to load
   * @returns The loaded CalendarEvent or null if loading failed
   */
  const loadEvent = async (eventId: string): Promise<CalendarEvent | null> => {
    try {
      const eventData = await ModelService.getModel(`/api/v1/events/${eventId}`);

      if (!eventData) {
        // Event not found
        state.err = 'error_event_not_found';
        // 'calendars' is the bare /calendar route, which resolves to the current calendar.
        router.push({ name: 'calendars' });
        return null;
      }

      return CalendarEvent.fromObject(eventData);
    }
    catch (error: any) {
      console.error('Error loading event:', error);

      // 401 handling is delegated to the global axios interceptor in
      // AuthenticationService, which queues the request and opens the
      // session-expired modal. We only handle 403 (no permission) locally.
      if (error.response?.status === 403) {
        router.push({ name: 'calendars' });
        return null;
      }

      state.err = 'error_loading_event';
      return null;
    }
  };

  /**
   * Initialize the event editor based on mode and route parameters
   *
   * @param eventIdProp - Optional event ID from component props
   * @param onLanguagesUpdate - Callback to update languages list
   * @param onLocationsFetch - Callback to fetch locations for a calendar
   * @param t - Translation function for a failure after the event loaded,
   *   where the form's error box renders `err` as text
   * @returns Promise that resolves when initialization is complete
   */
  const initializeEvent = async (
    eventIdProp?: string | null,
    onLanguagesUpdate?: (languages: string[]) => void,
    onLocationsFetch?: (calendarId: string) => Promise<void>,
    t?: (key: string) => string,
  ): Promise<void> => {
    try {
      state.isLoading = true;

      // Determine mode from route
      state.mode = determineMode(eventIdProp);
      state.isDuplicationMode = state.mode === 'duplicate';

      // Load available calendars
      state.availableCalendars = await calendarService.loadCalendars();

      if (state.availableCalendars.length === 0) {
        // No calendars, redirect to calendar creation
        router.push({ name: 'calendars' });
        return;
      }

      // Handle different modes
      if (state.mode === 'edit') {
        // Edit mode: Load existing event
        const eventId = (eventIdProp || route.params.eventId) as string;
        const loadedEvent = await loadEvent(eventId);

        if (!loadedEvent) {
          return; // Redirect handled in loadEvent
        }

        state.event = loadedEvent;
        // Ensure default language content exists
        state.event.content(defaultLanguage);

        // Initialize mediaId from loaded event so the image workspace renders
        if (loadedEvent.mediaId || loadedEvent.media?.id) {
          mediaId.value = loadedEvent.mediaId || loadedEvent.media!.id;
        }

        // Load categories for existing event
        try {
          const eventCategories = await categoryService.getEventCategories(loadedEvent.id);
          selectedCategories.value = eventCategories.map(cat => cat.id);
        }
        catch (error) {
          console.error('Error loading event categories:', error);
        }

        // Initialize series from the loaded event
        selectedSeriesId.value = loadedEvent.series?.id || null;
      }
      else if (state.mode === 'duplicate') {
        // Duplicate mode: Load source event and strip IDs
        const sourceEventId = route.query.from as string;
        const sourceEvent = await loadEvent(sourceEventId);

        if (!sourceEvent) {
          return; // Redirect handled in loadEvent
        }

        // Strip the event for duplication
        state.event = stripEventForDuplication(sourceEvent);
        // Ensure default language content exists
        state.event.content(defaultLanguage);

        // Load categories from the source event via the API, mirroring the edit mode approach.
        // This is more reliable than relying on sourceEvent.categories, which may be empty
        // if the API response did not include category data in the serialised event object.
        try {
          const eventCategories = await categoryService.getEventCategories(sourceEventId);
          selectedCategories.value = eventCategories.map(cat => cat.id);
        }
        catch (error) {
          console.error('Error loading source event categories:', error);
          // Fall back to categories already present on the loaded event model
          if (sourceEvent.categories && sourceEvent.categories.length > 0) {
            selectedCategories.value = sourceEvent.categories.map(cat => cat.id);
          }
        }

        // Preserve media reference
        if (sourceEvent.mediaId) {
          mediaId.value = sourceEvent.mediaId;
        }
        // Series is not copied on duplication — new event starts without a series
      }
      else {
        // Create mode: Initialize new event
        // Check for pre-selected calendar from store
        const preSelectedCalendar = calendarStore.selectedCalendar;

        if (preSelectedCalendar) {
          state.event = initializeNewEvent(preSelectedCalendar);
        }
        else if (state.availableCalendars.length === 1) {
          state.event = initializeNewEvent(state.availableCalendars[0]);
        }
        else {
          // Multiple calendars, use first one as default
          state.event = initializeNewEvent(state.availableCalendars[0]);
        }
      }

      // Update languages from event
      if (state.event) {
        const eventLanguages = state.event.getLanguages();
        eventLanguages.unshift(defaultLanguage);
        const uniqueLanguages = [...new Set(eventLanguages)];

        if (onLanguagesUpdate) {
          onLanguagesUpdate(uniqueLanguages);
        }

        // Set current calendar
        state.calendar = state.availableCalendars.find(c => c.id === state.event!.calendarId) || null;

        // Fetch locations for the calendar
        if (onLocationsFetch && state.event.calendarId) {
          await onLocationsFetch(state.event.calendarId);
        }
      }
    }
    catch (error) {
      console.error('Error initializing event editor:', error);
      // Before the event loads, the load-error view translates the key;
      // after, the form renders and shows err as-is, so translate here.
      state.err = state.event && t
        ? t('error_loading_calendars')
        : 'error_loading_calendars';
    }
    finally {
      state.isLoading = false;
    }
  };

  /**
   * Save the event (create or update)
   *
   * @param t - Translation function for error messages
   * @param onDirtyReset - Callback to reset dirty state after successful save
   * @returns Promise that resolves when save is complete
   */
  const saveEvent = async (
    t: (key: string) => string,
    onDirtyReset?: () => void,
  ): Promise<void> => {
    const model = state.event;

    if (!model) {
      state.err = t('error_no_event');
      state.errDetail = '';
      return;
    }

    // Clear previous validation errors
    state.err = '';
    state.errDetail = '';
    clearAllFieldErrors();

    // Ensure we have a calendarId
    if (!model.calendarId && state.availableCalendars.length > 0) {
      model.calendarId = state.availableCalendars[0].id;
    }

    if (!model.calendarId) {
      state.err = t('error_no_calendar');
      state.errDetail = '';
      return;
    }

    // Run client-side field validation
    if (!validateEvent(t)) {
      return;
    }

    try {
      if (mediaId.value) {
        model.mediaId = mediaId.value;
      }

      // Set locationId if a location has been selected
      // (The location object is only for display purposes in the UI)
      if (state.event.locationId) {
        model.locationId = state.event.locationId;
      }

      // Nullify empty location objects to prevent sending empty data to the server.
      // The form initializes events with an empty EventLocation for UI binding,
      // but the server rejects empty location objects with a 500 error.
      if (model.location && isEmptyLocation(model.location)) {
        model.location = null;
      }

      // Save the event
      const savedEvent = await eventService.saveEvent(model);

      // Save category assignments if any categories are selected
      if (selectedCategories.value.length > 0) {
        try {
          await categoryService.assignCategoriesToEvent(savedEvent.id, selectedCategories.value);
        }
        catch (categoryError) {
          console.error('Error saving event categories:', categoryError);
          // Don't fail the entire save for category errors
        }
      }

      // Save series assignment: assign or clear based on selection
      try {
        const previousSeriesId = model.series?.id || null;
        if (selectedSeriesId.value) {
          // Assign (or reassign) series to the event
          await seriesService.assignSeries(savedEvent.id, selectedSeriesId.value);
        }
        else if (previousSeriesId) {
          // Clear the existing series assignment
          await seriesService.clearSeries(savedEvent.id, previousSeriesId);
        }
      }
      catch (seriesError) {
        console.error('Error saving event series:', seriesError);
        // Don't fail the entire save for series errors
      }

      // Update selected calendar
      calendarStore.setSelectedCalendar(model.calendarId);

      // Reset dirty state after successful save to allow navigation
      if (onDirtyReset) {
        onDirtyReset();
      }

      // Show success toast before navigating away
      toast.success(t('event_saved_success'));

      // Navigate to the calendar view for this event's calendar
      const calendar = state.availableCalendars.find(c => c.id === model.calendarId);
      if (calendar) {
        router.push({
          name: 'calendar',
          params: { calendar: calendar.urlName },
        });
      }
      else {
        router.push({ name: 'calendars' });
      }
    }
    catch (error: any) {
      console.error('Error saving event:', error);
      state.err = t('error_saving_event');
      // Extract actionable detail from the server response if available
      const serverMessage = error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || '';
      state.errDetail = typeof serverMessage === 'string' ? serverMessage : '';

      // Parse server field-level errors if present
      const serverFields = error?.response?.data?.fields;
      if (serverFields && typeof serverFields === 'object') {
        for (const [field, message] of Object.entries(serverFields)) {
          if (typeof message === 'string') {
            fieldErrors[field] = message;
          }
        }
      }
    }
  };

  /**
   * Computed page title based on mode
   */
  const pageTitle = computed(() => {
    if (state.mode === 'edit') {
      return 'edit_event';
    }
    else if (state.mode === 'duplicate') {
      return 'duplicate_event';
    }
    else {
      return 'create_event';
    }
  });

  return {
    state,
    selectedCategories,
    selectedSeriesId,
    mediaId,
    fieldErrors,
    clearFieldError,
    initializeEvent,
    saveEvent,
    pageTitle,
  };
}
